import { useState } from 'react'
import { FileText, Loader2, Lock, Pencil, Plus, Trash2 } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  useCreateDocumentTemplate,
  useDeleteDocumentTemplate,
  useDocumentTemplates,
  useUpdateDocumentTemplate,
} from '@/features/documents/hooks'
import type { DocumentTemplate } from '@/types/documents'

const CATEGORIES = ['agreement', 'financial', 'ownership', 'registration', 'delivery']
const STAGES = ['prospecting', 'qualified', 'proposal', 'negotiation', 'won', 'lost']

// Merge fields BuildMergeData() provides; surfaced here so admins authoring
// template bodies know what they can interpolate.
const MERGE_FIELDS = [
  'today',
  'deal_title',
  'deal_value_usd',
  'deal_owner',
  'expected_close',
  'buyer_first_name',
  'buyer_last_name',
  'buyer_full_name',
  'buyer_company',
  'buyer_email',
  'buyer_phone',
  'boat_name',
  'boat_year',
  'boat_make',
  'boat_model',
  'boat_hin',
  'boat_length_ft',
  'boat_price_usd',
  'boat_engine',
]

interface TemplateFormValues {
  key: string
  name: string
  category: string
  body: string
  required_stage: string
}

function templateToFormValues(t: DocumentTemplate): TemplateFormValues {
  return { key: t.key, name: t.name, category: t.category, body: t.body, required_stage: t.required_stage }
}

function TemplateForm({
  defaultValues,
  onSubmit,
  isPending,
  submitLabel,
}: {
  defaultValues?: TemplateFormValues
  onSubmit: (values: TemplateFormValues) => void
  isPending: boolean
  submitLabel: string
}) {
  const [values, setValues] = useState<TemplateFormValues>(
    defaultValues ?? { key: '', name: '', category: 'agreement', body: '', required_stage: '' },
  )
  const set = (patch: Partial<TemplateFormValues>) => setValues((v) => ({ ...v, ...patch }))

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit(values)
      }}
    >
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="dt-name">Name</Label>
          <Input
            id="dt-name"
            value={values.name}
            required
            placeholder="Purchase & Sales Agreement"
            onChange={(e) => set({ name: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="dt-key">Key</Label>
          <Input
            id="dt-key"
            value={values.key}
            required
            pattern="[a-z0-9_]+"
            title="Lowercase letters, digits, underscores"
            placeholder="purchase_agreement"
            onChange={(e) => set({ key: e.target.value })}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="dt-category">Category</Label>
          <Select value={values.category} onValueChange={(v) => set({ category: v })}>
            <SelectTrigger id="dt-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => (
                <SelectItem key={c} value={c} className="capitalize">
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="dt-stage">Auto-create at stage</Label>
          <Select
            value={values.required_stage || 'none'}
            onValueChange={(v) => set({ required_stage: v === 'none' ? '' : v })}
          >
            <SelectTrigger id="dt-stage">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Manual only</SelectItem>
              {STAGES.map((s) => (
                <SelectItem key={s} value={s} className="capitalize">
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="dt-body">Body (HTML)</Label>
        <Textarea
          id="dt-body"
          value={values.body}
          required
          rows={12}
          className="font-mono text-xs"
          placeholder={`<h1>Receipt</h1>\n<p>Sold to {{.buyer_full_name}}: {{.boat_name}} for {{.deal_value_usd}}.</p>`}
          onChange={(e) => set({ body: e.target.value })}
        />
        <p className="text-xs text-muted-foreground">
          Merge fields: {MERGE_FIELDS.map((f) => `{{.${f}}}`).join(', ')}
        </p>
      </div>
      <DialogFooter>
        <Button type="submit" disabled={isPending}>
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {submitLabel}
        </Button>
      </DialogFooter>
    </form>
  )
}

/**
 * DocumentTemplatesTab manages the sales-paperwork templates: per-tenant
 * HTML documents with merge fields. Templates bound to a stage are created
 * automatically as pending checklist rows when a deal enters that stage.
 */
export function DocumentTemplatesTab() {
  const { data: templates, isLoading, isError } = useDocumentTemplates()
  const createMutation = useCreateDocumentTemplate()
  const updateMutation = useUpdateDocumentTemplate()
  const deleteMutation = useDeleteDocumentTemplate()

  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<DocumentTemplate | null>(null)
  const [deleting, setDeleting] = useState<DocumentTemplate | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const handleCreate = (values: TemplateFormValues) => {
    setFormError(null)
    createMutation.mutate(
      {
        key: values.key,
        name: values.name,
        category: values.category,
        body: values.body,
        required_stage: values.required_stage || undefined,
      },
      {
        onSuccess: () => setCreateOpen(false),
        onError: (err) =>
          setFormError(err instanceof Error ? err.message : 'Failed to create template'),
      },
    )
  }

  const handleUpdate = (values: TemplateFormValues) => {
    if (!editing) return
    setFormError(null)
    updateMutation.mutate(
      {
        id: editing.id,
        body: {
          key: values.key,
          name: values.name,
          category: values.category,
          body: values.body,
          required_stage: values.required_stage || undefined,
        },
      },
      {
        onSuccess: () => setEditing(null),
        onError: (err) =>
          setFormError(err instanceof Error ? err.message : 'Failed to update template'),
      },
    )
  }

  const handleDelete = () => {
    if (!deleting) return
    deleteMutation.mutate(deleting.id, { onSuccess: () => setDeleting(null) })
  }

  return (
    <>
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-4 w-4" /> Document templates
            </CardTitle>
            <CardDescription>
              Sales paperwork templates (purchase agreements, bills of sale, …) rendered with each
              deal's customer, boat, and pricing data. Stage-bound templates appear automatically as
              required documents on deals; generated documents keep their snapshot, so edits never
              change what was already issued.
            </CardDescription>
          </div>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> New Template
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading && (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          )}
          {isError && (
            <p className="py-6 text-center text-sm text-destructive">Failed to load templates.</p>
          )}
          {!isLoading && !isError && (!templates || templates.length === 0) && (
            <p className="py-12 text-center text-sm text-muted-foreground">
              No templates yet — create one to start generating paperwork.
            </p>
          )}
          {!isLoading && !isError && templates && templates.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Key</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead className="w-24 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-medium">
                      {t.name}
                      {t.is_system && (
                        <Badge variant="outline" className="ml-2 gap-1 text-[10px]">
                          <Lock className="h-2.5 w-2.5" /> system
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{t.key}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="capitalize">
                        {t.category}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {t.required_stage ? (
                        <span className="text-sm capitalize">{t.required_stage}</span>
                      ) : (
                        <span className="text-sm text-muted-foreground">Manual</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => setEditing(t)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleting(t)}
                        disabled={t.is_system}
                        className="text-destructive"
                        aria-label={
                          t.is_system
                            ? `${t.name} is a system template and cannot be deleted`
                            : `Delete ${t.name}`
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open)
          if (!open) setFormError(null)
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>New document template</DialogTitle>
            <DialogDescription>
              A full HTML document. The dealer letterhead lives in the body, so each template
              carries its own look.
            </DialogDescription>
          </DialogHeader>
          <TemplateForm onSubmit={handleCreate} isPending={createMutation.isPending} submitLabel="Create Template" />
          {formError && <p className="text-sm text-destructive">{formError}</p>}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={!!editing}
        onOpenChange={(open) => {
          if (!open) {
            setEditing(null)
            setFormError(null)
          }
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit template</DialogTitle>
            <DialogDescription>
              Editing a template only affects documents generated from now on — already-issued
              documents keep their original snapshot.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <TemplateForm
              defaultValues={templateToFormValues(editing)}
              onSubmit={handleUpdate}
              isPending={updateMutation.isPending}
              submitLabel="Save Changes"
            />
          )}
          {formError && <p className="text-sm text-destructive">{formError}</p>}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete template?</DialogTitle>
            <DialogDescription>
              This permanently deletes “{deleting?.name}”. Documents already generated from it are
              kept.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
